// Sprint 5 — Módulo de Clientes CRM

import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { X, CalendarPlus, UserCircle, Phone, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { AppointmentStatus } from "@prisma/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { labelAppointmentStatus } from "@/lib/dashboard-labels";
import { WhatsAppChatLink } from "@/components/dashboard/whatsapp-chat-link";
import { PatientProgramsSection } from "@/components/dashboard/patient-programs-section";
import { useConfirm } from "@/hooks/useConfirm";

export type ClientListRow = {
  clientId: string | null;
  email: string | null;
  displayName: string;
  phone: string | null;
  appointmentCount: number;
  internalNotes: string | null;
};

type HistoryItem = {
  id: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  serviceName: string;
};

function profQs(effectiveProfessionalId: string | null): string {
  return effectiveProfessionalId
    ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}`
    : "";
}

function initials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function avatarColor(name: string): string {
  const colors = [
    "linear-gradient(135deg, #6366f1, #818cf8)",
    "linear-gradient(135deg, #8b5cf6, #a78bfa)",
    "linear-gradient(135deg, #0ea5e9, #38bdf8)",
    "linear-gradient(135deg, #10b981, #34d399)",
    "linear-gradient(135deg, #f59e0b, #fbbf24)",
    "linear-gradient(135deg, #ef4444, #f87171)",
    "linear-gradient(135deg, #ec4899, #f472b6)",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function statusStyle(status: AppointmentStatus): { bg: string; color: string } {
  switch (status) {
    case "COMPLETED":
      return { bg: "#dcfce7", color: "#15803d" };
    case "CANCELLED":
      return { bg: "#fee2e2", color: "#b91c1c" };
    case "CONFIRMED":
      return { bg: "#dbeafe", color: "#1d4ed8" };
    case "NO_SHOW":
      return { bg: "#f1f5f9", color: "#64748b" };
    default:
      return { bg: "#fef9c3", color: "#a16207" };
  }
}

const sectionLabel = css({
  fontSize: "11px",
  fontWeight: "700",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#94a3b8",
  mb: "3",
});

export function ClientDetailDrawer({
  open,
  onClose,
  row,
  effectiveProfessionalId,
  onRefreshList,
  onOpenCreateProfile,
  onDeleted,
}: {
  open: boolean;
  onClose: () => void;
  row: ClientListRow | null;
  effectiveProfessionalId: string | null;
  onRefreshList: () => Promise<void>;
  onOpenCreateProfile: (email: string) => void;
  onDeleted?: () => void;
}) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const shouldReduceMotion = useReducedMotion();
  const [history, setHistory] = useState<HistoryItem[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editPhone, setEditPhone] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const pq = profQs(effectiveProfessionalId);

  useEffect(() => {
    if (!row) return;
    setEditPhone(row.phone ?? "");
    setEditNotes(row.internalNotes ?? "");
    setSaveMsg(null);
  }, [row?.clientId, row?.email, row?.phone, row?.internalNotes]);

  useEffect(() => {
    if (!open || !row) {
      setHistory(null);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    const qs = new URLSearchParams({ email: row.email ?? "" });
    if (effectiveProfessionalId) qs.set("professionalId", effectiveProfessionalId);
    void (async () => {
      const r = await fetchWithSessionSync(
        `/api/dashboard/clients/appointments?${qs.toString()}`,
        { credentials: "include" },
        refresh,
      );
      if (cancelled) return;
      setHistoryLoading(false);
      if (!r.ok) {
        setHistory([]);
        return;
      }
      const j = (await r.json()) as { items: HistoryItem[] };
      setHistory(j.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, row?.email, effectiveProfessionalId, refresh]);

  async function saveProfile() {
    if (!row?.clientId) return;
    setSaveBusy(true);
    setSaveMsg(null);
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients/${row.clientId}${pq}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: editPhone.trim() || null,
          internalNotes: editNotes.trim() || null,
        }),
      },
      refresh,
    );
    setSaveBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setSaveMsg(j.error ?? "No se pudo guardar");
      return;
    }
    await onRefreshList();
  }

  async function deleteClient() {
    if (!row?.clientId) return;
    if (
      !(await confirm({
        message: `¿Eliminar a "${row.displayName}"? Esta acción no se puede deshacer.`,
        tone: "danger",
        confirmLabel: "Eliminar",
      }))
    )
      return;
    setDeleteBusy(true);
    setDeleteError(null);
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients/${row.clientId}${pq}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    setDeleteBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setDeleteError(j.error ?? "No se pudo eliminar el cliente");
      return;
    }
    onDeleted?.();
    onClose();
  }

  function goCalendar() {
    if (!row) return;
    const calQs = effectiveProfessionalId
      ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}`
      : "";
    navigate(`/dashboard/calendar${calQs}`, {
      state: {
        prefillClientName: row.displayName,
        prefillClientEmail: row.email,
        prefillClientPhone: row.phone ?? "",
      },
    });
    onClose();
  }

  return (
    <AnimatePresence>
      {open && row ? (
        <motion.div
          key="client-drawer-overlay"
          className={css({
            position: "fixed",
            inset: 0,
            zIndex: 60,
            bg: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            justifyContent: "flex-end",
          })}
          initial={shouldReduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
          onClick={(e) => e.target === e.currentTarget && onClose()}
        >
          <motion.aside
            key="client-drawer-panel"
            className={css({
              width: { base: "100%", sm: "440px" },
              height: "100%",
              bg: "white",
              borderLeftWidth: "1px",
              borderColor: "#e2e8f0",
              boxShadow: "-8px 0 40px rgba(15,23,42,0.13)",
              display: "flex",
              flexDir: "column",
            })}
            initial={shouldReduceMotion ? false : { x: "100%" }}
            animate={{ x: 0 }}
            exit={shouldReduceMotion ? undefined : { x: "100%" }}
            transition={{ type: "tween", ease: [0.32, 0.72, 0, 1], duration: 0.28 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ─────────────────────────────── */}
            <div
              className={css({
                flexShrink: 0,
                borderBottomWidth: "1px",
                borderColor: "#f1f5f9",
                pb: "5",
              })}
            >
              {/* Close button */}
              <div className={css({ display: "flex", justifyContent: "flex-end", p: "4", pb: "0" })}>
                <button
                  type="button"
                  aria-label="Cerrar"
                  onClick={onClose}
                  className={css({
                    p: "1.5",
                    rounded: "lg",
                    border: "none",
                    bg: "transparent",
                    cursor: "pointer",
                    color: "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "opacity 120ms ease",
                    _hover: { opacity: 0.6 },
                    _active: { transform: "scale(0.93)" },
                  })}
                >
                  <X size={18} strokeWidth={2} aria-hidden />
                </button>
              </div>

              {/* Avatar + identidad */}
              <div
                className={css({
                  display: "flex",
                  flexDir: "column",
                  alignItems: "center",
                  gap: "2",
                  px: "6",
                  pb: "1",
                })}
              >
                <div
                  className={css({
                    w: "16",
                    h: "16",
                    rounded: "full",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "xl",
                    fontWeight: "800",
                    color: "white",
                    letterSpacing: "0.02em",
                    userSelect: "none",
                    mb: "1",
                  })}
                  style={{
                    background: avatarColor(row.displayName),
                    boxShadow: "0 4px 16px rgba(99,102,241,0.25)",
                  }}
                >
                  {initials(row.displayName)}
                </div>

                <div className={css({ textAlign: "center" })}>
                  <h2
                    className={css({
                      fontSize: "xl",
                      fontWeight: "700",
                      color: "#0f172a",
                      letterSpacing: "-0.01em",
                    })}
                  >
                    {row.displayName}
                  </h2>
                  {row.email && (
                    <p className={css({ fontSize: "sm", color: "#64748b", mt: "0.5" })}>{row.email}</p>
                  )}
                </div>

                {/* Badge de reservas */}
                <div
                  className={css({
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "1.5",
                    px: "3",
                    py: "1",
                    rounded: "full",
                    bg: row.appointmentCount > 0 ? "#ede9fe" : "#f1f5f9",
                    color: row.appointmentCount > 0 ? "#6d28d9" : "#94a3b8",
                    fontSize: "xs",
                    fontWeight: "600",
                    mt: "1",
                  })}
                >
                  <CalendarPlus size={11} aria-hidden />
                  {row.appointmentCount}{" "}
                  {row.appointmentCount === 1 ? "reserva" : "reservas"}
                </div>
              </div>

              {/* Quick actions */}
              <div
                className={css({
                  display: "flex",
                  gap: "2",
                  justifyContent: "center",
                  mt: "4",
                  px: "6",
                })}
              >
                <WhatsAppChatLink
                  phone={row.clientId ? editPhone.trim() || row.phone : row.phone}
                  variant="compact"
                />
                <button
                  type="button"
                  onClick={goCalendar}
                  className={css({
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "1.5",
                    px: "3",
                    py: "1.5",
                    rounded: "md",
                    fontSize: "xs",
                    fontWeight: "600",
                    color: "#334155",
                    bg: "#f8fafc",
                    borderWidth: "1px",
                    borderColor: "#e2e8f0",
                    cursor: "pointer",
                    transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 120ms ease",
                    _hover: { opacity: 0.75 },
                    _active: { transform: "scale(0.96)" },
                  })}
                >
                  <CalendarPlus size={13} strokeWidth={2} aria-hidden />
                  Agendar turno
                </button>
              </div>
            </div>

            {/* ── Body scrollable ─────────────────────── */}
            <div
              className={css({
                flex: "1",
                overflowY: "auto",
                p: "5",
                display: "flex",
                flexDir: "column",
                gap: "6",
              })}
            >
              {/* Sección: perfil editable / sin perfil */}
              {row.clientId ? (
                <section>
                  <p className={sectionLabel}>Perfil</p>
                  <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
                    <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                      <span
                        className={css({
                          fontSize: "xs",
                          fontWeight: "500",
                          color: "#64748b",
                          display: "flex",
                          alignItems: "center",
                          gap: "1",
                        })}
                      >
                        <Phone size={11} aria-hidden />
                        Teléfono
                      </span>
                      <Input
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                        placeholder="Sin teléfono"
                      />
                    </label>

                    <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                      <span className={css({ fontSize: "xs", fontWeight: "500", color: "#64748b" })}>
                        Notas internas
                      </span>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Notas privadas sobre este cliente…"
                        className={css({
                          padding: "10px 12px",
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          minHeight: "88px",
                          fontSize: "sm",
                          color: "#0f172a",
                          bg: "#f8fafc",
                          resize: "vertical",
                          _placeholder: { color: "#94a3b8" },
                          _focus: {
                            outline: "none",
                            borderColor: "#6366f1",
                            bg: "white",
                            boxShadow: "0 0 0 3px rgba(99,102,241,0.1)",
                          },
                        })}
                      />
                    </label>

                    {saveMsg ? (
                      <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{saveMsg}</p>
                    ) : null}

                    <Button
                      variant="primary"
                      disabled={saveBusy}
                      onClick={() => void saveProfile()}
                      className={css({ alignSelf: "flex-start" })}
                    >
                      {saveBusy ? "Guardando…" : "Guardar cambios"}
                    </Button>
                  </div>
                </section>
              ) : (
                <section>
                  <p className={sectionLabel}>Perfil</p>
                  <div
                    className={css({
                      p: "4",
                      rounded: "xl",
                      bg: "#f8fafc",
                      borderWidth: "1px",
                      borderColor: "#e2e8f0",
                      display: "flex",
                      gap: "3",
                      alignItems: "flex-start",
                    })}
                  >
                    <div
                      className={css({
                        w: "9",
                        h: "9",
                        rounded: "lg",
                        bg: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      })}
                    >
                      <UserCircle size={18} strokeWidth={1.5} className={css({ color: "#94a3b8" })} />
                    </div>
                    <div className={css({ flex: "1", minW: 0 })}>
                      <p
                        className={css({
                          fontSize: "sm",
                          fontWeight: "600",
                          color: "#334155",
                          mb: "1",
                        })}
                      >
                        Sin perfil CRM
                      </p>
                      {row.phone ? (
                        <p className={css({ fontSize: "sm", color: "#64748b", mb: "1.5" })}>
                          Teléfono en reservas:{" "}
                          <span className={css({ fontWeight: "500", color: "#334155" })}>
                            {row.phone}
                          </span>
                        </p>
                      ) : null}
                      <p className={css({ fontSize: "sm", color: "#94a3b8", mb: "3" })}>
                        Creá un perfil para guardar teléfono y notas internas.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={() => onOpenCreateProfile(row.email ?? "")}
                      >
                        Crear perfil
                      </Button>
                    </div>
                  </div>
                </section>
              )}

              {/* Sección: programas de tratamiento (requiere perfil CRM) */}
              {row.clientId ? (
                <section>
                  <p className={sectionLabel}>Programas de tratamiento</p>
                  <PatientProgramsSection clientId={row.clientId} effectiveProfessionalId={effectiveProfessionalId} />
                </section>
              ) : null}

              {/* Sección: historial */}
              <section>
                <p className={sectionLabel}>Historial de turnos</p>

                {historyLoading ? (
                  <div
                    className={css({
                      display: "flex",
                      flexDir: "column",
                      gap: "3",
                    })}
                  >
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={css({
                          h: "12",
                          rounded: "lg",
                          bg: "#f1f5f9",
                          animation: "pulse 1.5s ease-in-out infinite",
                        })}
                      />
                    ))}
                  </div>
                ) : !history?.length ? (
                  <div
                    className={css({
                      py: "8",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "sm",
                    })}
                  >
                    Sin turnos registrados para este contacto.
                  </div>
                ) : (
                  <div
                    className={css({
                      borderWidth: "1px",
                      borderColor: "#f1f5f9",
                      rounded: "xl",
                      overflow: "hidden",
                    })}
                  >
                    {history.map((h, i) => {
                      const sc = statusStyle(h.status);
                      return (
                        <div
                          key={h.id}
                          className={css({
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "3",
                            px: "4",
                            py: "3",
                            borderTopWidth: i === 0 ? "0" : "1px",
                            borderColor: "#f8fafc",
                            bg: "white",
                          })}
                        >
                          <div className={css({ minW: 0 })}>
                            <p
                              className={css({
                                fontSize: "sm",
                                fontWeight: "500",
                                color: "#0f172a",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              })}
                            >
                              {h.serviceName}
                            </p>
                            <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                              {new Date(h.startTime).toLocaleString("es-AR", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })}
                            </p>
                          </div>
                          <span
                            className={css({
                              fontSize: "11px",
                              fontWeight: "600",
                              px: "2.5",
                              py: "1",
                              rounded: "full",
                              flexShrink: 0,
                              whiteSpace: "nowrap",
                            })}
                            style={{ background: sc.bg, color: sc.color }}
                          >
                            {labelAppointmentStatus(h.status)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* Zona peligrosa: eliminar cliente */}
              {row.clientId && (
                <section>
                  <p className={sectionLabel}>Zona peligrosa</p>
                  {deleteError && (
                    <div
                      className={css({
                        mb: "3",
                        px: "3",
                        py: "2.5",
                        rounded: "lg",
                        bg: "#fef2f2",
                        borderWidth: "1px",
                        borderColor: "#fecaca",
                        fontSize: "sm",
                        color: "#b91c1c",
                      })}
                    >
                      {deleteError}
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={deleteBusy}
                    onClick={() => void deleteClient()}
                    className={css({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "1.5",
                      px: "3",
                      py: "2",
                      rounded: "lg",
                      fontSize: "sm",
                      fontWeight: "600",
                      color: "#b91c1c",
                      bg: "#fef2f2",
                      borderWidth: "1px",
                      borderColor: "#fecaca",
                      cursor: "pointer",
                      transition: "opacity 120ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)",
                      _hover: { bg: "#fee2e2" },
                      _active: { transform: "scale(0.97)" },
                      _disabled: { opacity: 0.5, cursor: "not-allowed" },
                    })}
                  >
                    <Trash2 size={13} strokeWidth={2} aria-hidden />
                    {deleteBusy ? "Eliminando…" : "Eliminar cliente"}
                  </button>
                </section>
              )}
            </div>
          </motion.aside>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
